import { AxiosResponse } from 'axios'
import { NestAPI } from '../../nestapi'
import {
  BusinessManagerService,
  FacebookAssetNames,
  FBAssignAssetDTO,
  FBAssignAssetResult,
  FBBusiness,
  FBBusinessAdAccount,
  FBBusinessIGAccount,
  FBBusinessPage,
  SystemUserIDs,
} from './BusinessManager.types'
import {
  AuthorizerConnection,
  AuthorizerConnectionsByUser,
  AuthorizerTokenStatus,
  Business,
  BusinessConfig,
  BusinessError,
  BusinessFBObjects,
  FacebookPixel,
  FBConnectedInstagramAccount,
  MatchRate,
  SystemUserBusinessConnection,
} from '@marketing-milk/interfaces'
import { MMFacebookVerificationFields } from 'pages/business/accounts/builder/pages/edit/info/tabs/social/facebook'

export class BusinessManagerServiceImplementation implements BusinessManagerService {
  getInstagramPagesByAccountID(fbAccountID: string): Promise<FBConnectedInstagramAccount[]> {
    return NestAPI.get(`fb-accounts/instagram`, {
      params: {
        fbAccountID,
      },
    })
  }

  async createBusiness(businessConfig: BusinessConfig): Promise<Business> {
    const response: Business = await NestAPI.post(`businesses/manage`, businessConfig)
    return response
  }

  async updateBusiness(businessID: number, businessConfig: BusinessConfig): Promise<Business> {
    const response: Business = await NestAPI.patch(
      `businesses/manage/${businessID}`,
      businessConfig
    )
    return response
  }

  async getHistoricalMatchRate(businessID: number, eventType: string): Promise<MatchRate> {
    const response: AxiosResponse = await NestAPI.get(
      `businesses/${businessID}/funnel/historical-match-rate/${eventType}`
    )

    return response.data
  }

  async getFBConnectedAccounts(
    fbAccountID: string,
    businessID?: number,
    adAuthorizerID?: number
  ): Promise<BusinessFBObjects> {
    const response: BusinessFBObjects = await NestAPI.get(`businesses/manage/fb-objects`, {
      params: {
        fb_account_id: fbAccountID,
        business_id: businessID,
        fb_ad_authorizer_id: adAuthorizerID,
      },
    })

    return response
  }

  async resolveIssueWithBusiness(businessID: number, flag: BusinessError): Promise<boolean> {
    return await NestAPI.patch(`businesses/manage/${businessID}/remove-flag`, {
      flag,
    })
  }

  async checkFbAccess(businessID: number): Promise<BusinessError[]> {
    return await NestAPI.post(`businesses/manage/${businessID}/check-fb-access`)
  }

  async getFBBusinesses(): Promise<FBBusiness[]> {
    const response: FBBusiness[] = await NestAPI.get(`businesses/manage/fb-businesses`)

    return response
  }

  async getFBPages(search?: string, limit?: number): Promise<FBBusinessPage[]> {
    const response: FBBusinessPage[] = await NestAPI.get(`businesses/manage/fb-pages`, {
      params: { search, limit },
    })

    return response
  }

  async getFBAdAccounts(search?: string, limit?: number): Promise<FBBusinessAdAccount[]> {
    const response: FBBusinessAdAccount[] = await NestAPI.get(`businesses/manage/fb-ad-accounts`, {
      params: { search, limit },
    })

    return response
  }

  async getFBDatasets(fbAccountID: string): Promise<FacebookPixel[]> {
    const response: FacebookPixel[] = await NestAPI.get(`businesses/manage/fb-datasets`, {
      params: {
        fb_account_id: fbAccountID,
      },
    })

    return response
  }

  async getFBIGAccounts(fbAccountID: string): Promise<FBBusinessIGAccount[]> {
    const response: FBBusinessIGAccount[] = await NestAPI.get(`businesses/manage/fb-ig-accounts`, {
      params: {
        fb_account_id: fbAccountID,
      },
    })

    return response
  }

  async verifyFBConnection(
    verify: MMFacebookVerificationFields
  ): Promise<
    SystemUserIDs & FacebookAssetNames & { warnings: { flag: BusinessError; message: string }[] }
  > {
    return await NestAPI.post(`businesses/manage/fb-verify`, verify)
  }

  async assignFacebookAsset(dto: FBAssignAssetDTO): Promise<FBAssignAssetResult> {
    return await NestAPI.post(`businesses/manage/fb-assign`, dto)
  }

  async checkDatasetConnection(
    datasetID: string,
    adAccountID: string
  ): Promise<{ connected: boolean }> {
    return await NestAPI.get(`businesses/manage/fb-dataset-connection-check`, {
      params: { dataset_id: datasetID, ad_account_id: adAccountID },
    })
  }

  async clearFacebookAccessCache() {
    await NestAPI.post(`businesses/manage/clear-fb-access-cache`)
  }

  async getMyAuthorizations(): Promise<AuthorizerConnection[]> {
    return NestAPI.get(`businesses/manage/my-authorizations`)
  }

  async getAllAuthorizations(): Promise<AuthorizerConnectionsByUser[]> {
    return NestAPI.get(`businesses/manage/authorizations`)
  }

  async getMyTokenStatus(): Promise<AuthorizerTokenStatus> {
    return NestAPI.get(`businesses/manage/my-token-status`)
  }

  async getAuthorizerTokenStatuses(): Promise<AuthorizerTokenStatus[]> {
    return NestAPI.get(`businesses/manage/authorizations/token-statuses`)
  }

  async clearErrorsForAuthorizer(userID: number): Promise<void> {
    await NestAPI.post(`businesses/manage/authorizations/${userID}/clear-errors`)
  }

  async getSystemUserBusinesses(): Promise<SystemUserBusinessConnection[]> {
    return NestAPI.get(`businesses/manage/system-user-businesses`)
  }
}
